import { useEffect, useState, useRef } from 'react';
import { useToast, Toast, ToastType } from '../../contexts/ToastContext';

const CONFIG: Record<ToastType, {
  icon: string;
  iconBg: string;
  iconColor: string;
  progressColor: string;
  borderColor: string;
}> = {
  success: {
    icon: 'ri-checkbox-circle-fill',
    iconBg: 'bg-green-50',
    iconColor: 'text-green-500',
    progressColor: 'bg-green-500',
    borderColor: 'border-green-100',
  },
  error: {
    icon: 'ri-close-circle-fill',
    iconBg: 'bg-red-50',
    iconColor: 'text-red-500',
    progressColor: 'bg-red-500',
    borderColor: 'border-red-100',
  },
  cart: {
    icon: 'ri-shopping-cart-2-fill',
    iconBg: 'bg-[#FFF5F0]',
    iconColor: 'text-[#FF6B35]',
    progressColor: 'bg-[#FF6B35]',
    borderColor: 'border-orange-100',
  },
  info: {
    icon: 'ri-information-fill',
    iconBg: 'bg-[#FFF5F0]',
    iconColor: 'text-[#FF6B35]',
    progressColor: 'bg-[#FF6B35]',
    borderColor: 'border-orange-100',
  },
  warning: {
    icon: 'ri-error-warning-fill',
    iconBg: 'bg-yellow-50',
    iconColor: 'text-yellow-500',
    progressColor: 'bg-yellow-500',
    borderColor: 'border-yellow-100',
  },
};

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

function ToastItem({ toast, onRemove }: ToastItemProps) {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(100);
  const cfg = CONFIG[toast.type];
  const duration = toast.duration ?? 3500;
  const startTime = useRef(Date.now());
  const rafRef = useRef<number>(0);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const enterFrame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setVisible(true));
    });

    const tick = () => {
      const elapsed = Date.now() - startTime.current;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      if (remaining > 0) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    dismissTimerRef.current = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onRemove(toast.id), 320);
    }, duration);

    return () => {
      cancelAnimationFrame(enterFrame);
      cancelAnimationFrame(rafRef.current);
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, [toast.id, duration, onRemove]);

  const handleClose = () => {
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    cancelAnimationFrame(rafRef.current);
    setVisible(false);
    setTimeout(() => onRemove(toast.id), 320);
  };

  return (
    <div
      className={`w-[340px] bg-white border rounded-2xl overflow-hidden transition-all duration-300 ease-out ${cfg.borderColor} ${
        visible
          ? 'opacity-100 translate-y-0 scale-100'
          : 'opacity-0 translate-y-4 scale-95'
      }`}
    >
      <div className="flex items-start gap-3 px-4 py-3.5">
        {/* Icon */}
        <div className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${cfg.iconBg}`}>
          <i className={`${cfg.icon} text-lg ${cfg.iconColor}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 py-0.5">
          <p className="text-sm font-semibold text-gray-900 leading-snug">{toast.title}</p>
          {toast.message && (
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{toast.message}</p>
          )}
        </div>

        {/* Close */}
        <button
          onClick={handleClose}
          className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-gray-500 transition-colors cursor-pointer flex-shrink-0 mt-0.5 rounded-full hover:bg-gray-100"
        >
          <i className="ri-close-line text-sm" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-0.5 bg-gray-100">
        <div
          className={`h-full ${cfg.progressColor} transition-none`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

export default function ToastContainer() {
  const { toasts, removeToast } = useToast();

  return (
    <div className="fixed top-6 right-6 z-[300] flex flex-col gap-3 pointer-events-none">
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem toast={toast} onRemove={removeToast} />
        </div>
      ))}
    </div>
  );
}
